type RawCampProgram = {
  id?: string;
  name?: string;
  description?: string | null;
  campType?: string;
  location?: string | null;
  minGrade?: string;
  maxGrade?: string;
  genderEligibility?: string;
  pricePerSessionCents?: number;
  siblingDiscountCents?: number | null;
  multiSessionDiscountPct?: number | null;
  registrationOpen?: string;
  registrationClose?: string;
  waiverText?: string | null;
  packingList?: string | null;
  status?: 'draft' | 'published' | 'archived';
};

type RawCampSession = {
  id?: string;
  programId?: string;
  name?: string | null;
  startDate?: string;
  endDate?: string;
  dailyStartTime?: string;
  dailyEndTime?: string;
  capacity?: number;
  enrolledCount?: number;
  waitlistCount?: number;
};

type RawEligibleStudent = {
  studentId?: string;
  firstName?: string;
  lastName?: string;
  preferredName?: string | null;
  grade?: string;
};

type RawCampProgramView = {
  program?: RawCampProgram;
  sessions?: RawCampSession[];
  spotsRemaining?: number;
  waitlistOnly?: boolean;
  isRegistered?: boolean;
  registrationClosed?: boolean;
};

type RawCampPricingSummary = {
  subtotalCents?: number;
  discountCents?: number;
  totalCents?: number;
  amountDueCents?: number;
};

type RawCampRegistrationDraft = {
  id?: string;
  selections?: Array<{
    studentId?: string;
    studentName?: string;
    sessionIds?: string[];
    priceCents?: number;
  }>;
  waiverAccepted?: boolean;
};

type RawCampRegistration = {
  id?: string;
  programId?: string;
  programName?: string;
  sessionIds?: string[];
  studentId?: string;
  studentName?: string;
  status?: string;
  paymentStatus?: string;
  grossCents?: number;
  amountPaidCents?: number;
  refundAmountCents?: number;
  stripePaymentIntentId?: string | null;
};

type RawCampWaitlistEntry = {
  id?: string;
  programId?: string;
  sessionId?: string;
  studentName?: string;
  position?: number;
  status?: string;
  createdAt?: number;
};

type RawParentCampCatalogResponse = {
  familyId?: string;
  familyName?: string;
  eligibleStudents?: RawEligibleStudent[];
  registrationSummary?: {
    registeredCount?: number;
    waitlistedCount?: number;
    openRegistrationCount?: number;
    amountDueCents?: number;
  };
  items?: RawCampProgramView[];
};

type RawParentCampRegistrationResponse = {
  familyId?: string;
  familyName?: string;
  program?: RawCampProgram;
  sessions?: RawCampSession[];
  eligibleStudents?: RawEligibleStudent[];
  draft?: RawCampRegistrationDraft | null;
  pricingSummary?: RawCampPricingSummary;
  paymentReadiness?: {
    ready?: boolean;
    reason?: string | null;
  };
};

type RawCampRegistrationListResponse = {
  items?: RawCampRegistration[];
  totalCount?: number;
  nextToken?: string | null;
};

type RawCampWaitlistListResponse = {
  items?: RawCampWaitlistEntry[];
  totalCount?: number;
  nextToken?: string | null;
};

type RawSchoolCampDashboardResponse = {
  summary?: {
    activeProgramsCount?: number;
    enrolledCampersCount?: number;
    openSeatsCount?: number;
    waitlistCount?: number;
    unpaidBalanceCents?: number;
  };
  items?: RawCampProgramView[];
  ingestHealth?: {
    state?: string;
    lastRefreshAt?: number | null;
    sourceSystem?: string;
  };
  waitlistPressure?: {
    totalWaitlisted?: number;
    sessionsAtCapacity?: number;
  };
  rosterReadiness?: {
    readyPrograms?: number;
    pendingPrograms?: number;
  };
  opsTelemetry?: {
    latestRefreshAt?: number | null;
    lastSnapshotDate?: string | null;
    sourceSystem?: string;
  };
  eventResponseSummary?: {
    pendingCommunications?: number;
    pendingWaitlistPromotions?: number;
  };
};

type RawSchoolCampProgramDetailResponse = {
  program?: RawCampProgram;
  sessions?: RawCampSession[];
  registrations?: RawCampRegistrationListResponse;
  waitlist?: RawCampWaitlistListResponse;
  financeSummary?: {
    collectedCents?: number;
    outstandingCents?: number;
    refundedCents?: number;
  };
  communicationsSummary?: {
    lastSentAt?: number | null;
    pendingCount?: number;
  };
  ingestHealth?: {
    state?: string;
    lastRefreshAt?: number | null;
    sourceSystem?: string;
  };
  exportAudit?: {
    lastExportAt?: number | null;
    lastExportFormat?: string | null;
    lastExportStatus?: string | null;
  };
};

type RawCampProgramEditorResponse = {
  program?: RawCampProgram | null;
  sessions?: RawCampSession[];
  defaults?: {
    gradeOptions?: string[];
    genderEligibilityOptions?: string[];
    campTypeOptions?: string[];
  };
  ingestHealth?: {
    state?: string;
    lastRefreshAt?: number | null;
    sourceSystem?: string;
  };
};

const todayIso = (): string => new Date().toISOString().slice(0, 10);

const formatGradeRange = (program?: RawCampProgram): string | undefined => {
  if (!program?.minGrade || !program?.maxGrade) {
    return undefined;
  }
  return program.minGrade === program.maxGrade
    ? program.minGrade
    : `${program.minGrade}-${program.maxGrade}`;
};

const formatCampType = (campType?: string): string | undefined => {
  if (!campType) {
    return undefined;
  }
  return campType
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (value) => value.toUpperCase());
};

const formatDateLabel = (timestampSeconds?: number | null): string | undefined => {
  if (!timestampSeconds) {
    return undefined;
  }
  return new Date(timestampSeconds * 1000).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const buildSessionLabel = (session?: RawCampSession): string | undefined => {
  if (!session) {
    return undefined;
  }
  if (session.name) {
    return session.name;
  }
  if (session.startDate && session.endDate) {
    return `${session.startDate} - ${session.endDate}`;
  }
  return session.startDate ?? session.endDate;
};

const buildCatalogProgramAndSessionLookups = (rawCatalog?: unknown): {
  programsById: Map<string, string>;
  sessionsById: Map<string, RawCampSession>;
} => {
  const data = (rawCatalog ?? {}) as RawParentCampCatalogResponse;
  const programsById = new Map<string, string>();
  const sessionsById = new Map<string, RawCampSession>();

  (data.items ?? []).forEach((item) => {
    const programId = item.program?.id ?? '';
    if (programId) {
      programsById.set(programId, item.program?.name ?? 'Camp Program');
    }
    (item.sessions ?? []).forEach((session) => {
      const sessionId = session.id ?? '';
      if (sessionId) {
        sessionsById.set(sessionId, session);
      }
    });
  });

  return { programsById, sessionsById };
};

const buildSessionLabelFromIds = (
  sessionIds: string[],
  sessionsById: Map<string, RawCampSession>,
): string | undefined => {
  const labels = sessionIds
    .map((sessionId) => buildSessionLabel(sessionsById.get(sessionId)))
    .filter((label): label is string => Boolean(label));

  return labels.length > 0 ? labels.join(', ') : undefined;
};

const buildEligibleStudentName = (student?: RawEligibleStudent): string => {
  if (!student) {
    return 'Unnamed Camper';
  }
  const preferred = String(student.preferredName ?? '').trim();
  if (preferred) {
    return preferred;
  }
  const first = String(student.firstName ?? '').trim();
  const last = String(student.lastName ?? '').trim();
  return [first, last].filter(Boolean).join(' ') || 'Unnamed Camper';
};

const buildRegistrationStatus = (item?: RawCampProgramView): string => {
  if (item?.isRegistered) {
    return 'registered';
  }
  if (item?.registrationClosed) {
    return 'closed';
  }
  if (item?.waitlistOnly) {
    return 'waitlist';
  }
  const spotsRemaining = item?.spotsRemaining ?? 0;
  if (spotsRemaining > 0 && spotsRemaining <= 5) {
    return 'few-seats';
  }
  return 'open';
};

const buildDeadlineAlert = (items: RawCampProgramView[]): string | undefined => {
  const current = todayIso();
  const closingSoon = items
    .map((item) => item.program)
    .filter((program): program is RawCampProgram => Boolean(program?.registrationClose && program.name))
    .filter((program) => program.registrationClose! >= current)
    .sort((left, right) => left.registrationClose!.localeCompare(right.registrationClose!))[0];

  if (!closingSoon?.registrationClose) {
    return undefined;
  }

  const daysUntilClose = Math.ceil(
    (new Date(closingSoon.registrationClose).getTime() - new Date(current).getTime()) / 86400000
  );
  if (daysUntilClose > 7) {
    return undefined;
  }

  return `${closingSoon.name} closes on ${closingSoon.registrationClose}.`;
};

const sumBalanceDue = (registration?: RawCampRegistration): number => Math.max(
  0,
  (registration?.grossCents ?? 0) - (registration?.amountPaidCents ?? 0) - (registration?.refundAmountCents ?? 0)
);

export type ParentCatalogProgramViewModel = {
  programId: string;
  name: string;
  campType?: string;
  gradeRange?: string;
  location?: string;
  sessionsCount: number;
  spotsRemaining: number;
  priceCents: number;
  registrationStatus: string;
};

export type ParentCatalogResponseViewModel = {
  familyId?: string;
  familyName?: string;
  seasonLabel?: string;
  eligibleStudents: Array<{ studentId: string; name: string; grade?: string }>;
  registrationSummary: {
    registeredCampers: number;
    waitlistedEntries: number;
    openCampWindows: number;
    amountDueCents: number;
  };
  items: ParentCatalogProgramViewModel[];
  deadlineAlert?: string;
};

export const mapParentCatalogResponse = (raw: unknown): ParentCatalogResponseViewModel => {
  const data = (raw ?? {}) as RawParentCampCatalogResponse;
  const items = (data.items ?? []).map((item) => ({
    programId: item.program?.id ?? '',
    name: item.program?.name ?? 'Untitled Program',
    campType: formatCampType(item.program?.campType),
    gradeRange: formatGradeRange(item.program),
    location: item.program?.location ?? undefined,
    sessionsCount: item.sessions?.length ?? 0,
    spotsRemaining: item.spotsRemaining ?? 0,
    priceCents: item.program?.pricePerSessionCents ?? 0,
    registrationStatus: buildRegistrationStatus(item),
  }));

  return {
    familyId: data.familyId,
    familyName: data.familyName,
    seasonLabel: items.length > 0 ? 'Current camp season' : undefined,
    eligibleStudents: (data.eligibleStudents ?? []).map((student) => ({
      studentId: student.studentId ?? '',
      name: buildEligibleStudentName(student),
      grade: student.grade,
    })),
    registrationSummary: {
      registeredCampers: data.registrationSummary?.registeredCount ?? 0,
      waitlistedEntries: data.registrationSummary?.waitlistedCount ?? 0,
      openCampWindows: data.registrationSummary?.openRegistrationCount ?? 0,
      amountDueCents: data.registrationSummary?.amountDueCents ?? 0,
    },
    items,
    deadlineAlert: buildDeadlineAlert(data.items ?? []),
  };
};

export type ParentRegistrationResponseViewModel = {
  familyId?: string;
  familyName?: string;
  program?: {
    programId: string;
    name: string;
    campType?: string;
    location?: string;
    gradeRange?: string;
    pricePerSessionCents: number;
  };
  sessions: Array<{
    sessionId: string;
    label?: string;
    startDate?: string;
    endDate?: string;
    capacity?: number;
    enrolled?: number;
    spotsRemaining?: number;
  }>;
  eligibleStudents: Array<{
    studentId: string;
    name: string;
    grade?: string;
  }>;
  draft?: {
    id?: string;
    selections: Array<{ studentId: string; sessionIds: string[] }>;
    selectedStudentIds: string[];
    selectedSessionIds: string[];
    waiverAccepted: boolean;
  };
  pricingSummary?: RawCampPricingSummary;
  paymentReadiness?: { ready?: boolean; reason?: string | null };
};

export const mapParentRegistrationResponse = (raw: unknown): ParentRegistrationResponseViewModel => {
  const data = (raw ?? {}) as RawParentCampRegistrationResponse;
  const draftSelections = (data.draft?.selections ?? [])
    .map((selection) => ({
      studentId: selection.studentId ?? '',
      sessionIds: (selection.sessionIds ?? []).filter(Boolean),
    }))
    .filter((selection) => selection.studentId && selection.sessionIds.length > 0);

  const selectedSessionIds = Array.from(
    new Set(draftSelections.flatMap((selection) => selection.sessionIds))
  );

  return {
    familyId: data.familyId,
    familyName: data.familyName,
    program: data.program
      ? {
          programId: data.program.id ?? '',
          name: data.program.name ?? 'Camp Registration',
          campType: formatCampType(data.program.campType),
          location: data.program.location ?? undefined,
          gradeRange: formatGradeRange(data.program),
          pricePerSessionCents: data.program.pricePerSessionCents ?? 0,
        }
      : undefined,
    sessions: (data.sessions ?? []).map((session) => ({
      sessionId: session.id ?? '',
      label: buildSessionLabel(session),
      startDate: session.startDate,
      endDate: session.endDate,
      capacity: session.capacity,
      enrolled: session.enrolledCount,
      spotsRemaining: session.capacity != null && session.enrolledCount != null
        ? Math.max(0, session.capacity - session.enrolledCount)
        : undefined,
    })),
    eligibleStudents: (data.eligibleStudents ?? []).map((student) => ({
      studentId: student.studentId ?? '',
      name: buildEligibleStudentName(student),
      grade: student.grade,
    })),
    draft: data.draft
      ? {
          id: data.draft.id,
          selections: draftSelections,
          selectedStudentIds: draftSelections.map((selection) => selection.studentId),
          selectedSessionIds,
          waiverAccepted: data.draft.waiverAccepted ?? false,
        }
      : undefined,
    pricingSummary: data.pricingSummary,
    paymentReadiness: data.paymentReadiness,
  };
};

export type RegistrationItemViewModel = {
  id: string;
  programId?: string;
  programName?: string;
  studentName?: string;
  sessionIds: string[];
  sessionLabel?: string;
  status?: string;
  paymentStatus?: string;
  amountPaidCents?: number;
  amountDueCents: number;
  grossCents?: number;
  refundAmountCents?: number;
  stripePaymentIntentId?: string | null;
};

export const mapCampRegistrationListResponse = (
  raw: unknown,
  rawCatalog?: unknown,
): { items: RegistrationItemViewModel[]; totalCount: number; nextToken?: string | null } => {
  const data = (raw ?? {}) as RawCampRegistrationListResponse;
  const { programsById, sessionsById } = buildCatalogProgramAndSessionLookups(rawCatalog);
  const items = (data.items ?? []).map((item) => ({
    id: item.id ?? '',
    programId: item.programId,
    programName: item.programName ?? programsById.get(item.programId ?? '') ?? 'Camp Program',
    studentName: item.studentName,
    sessionIds: item.sessionIds ?? [],
    sessionLabel: buildSessionLabelFromIds(item.sessionIds ?? [], sessionsById),
    status: item.status,
    paymentStatus: item.paymentStatus,
    amountPaidCents: item.amountPaidCents ?? 0,
    amountDueCents: sumBalanceDue(item),
    grossCents: item.grossCents ?? 0,
    refundAmountCents: item.refundAmountCents ?? 0,
    stripePaymentIntentId: item.stripePaymentIntentId ?? null,
  }));

  return {
    items,
    totalCount: data.totalCount ?? items.length,
    nextToken: data.nextToken,
  };
};

export type WaitlistItemViewModel = {
  waitlistEntryId: string;
  programId?: string;
  programName?: string;
  sessionLabel?: string;
  studentName?: string;
  position?: number;
  status?: string;
  joinedAt?: string;
};

export const mapCampWaitlistListResponse = (
  rawWaitlist: unknown,
  rawCatalog?: unknown
): { items: WaitlistItemViewModel[]; totalCount: number; nextToken?: string | null } => {
  const data = (rawWaitlist ?? {}) as RawCampWaitlistListResponse;
  const { programsById, sessionsById } = buildCatalogProgramAndSessionLookups(rawCatalog);
  const items = (data.items ?? []).map((item) => ({
    waitlistEntryId: item.id ?? '',
    programId: item.programId,
    programName: programsById.get(item.programId ?? '') ?? 'Camp Program',
    sessionLabel: buildSessionLabel(sessionsById.get(item.sessionId ?? '')),
    studentName: item.studentName,
    position: item.position,
    status: item.status === 'active'
      ? 'waiting'
      : item.status === 'promoted'
        ? 'offered'
        : item.status,
    joinedAt: formatDateLabel(item.createdAt),
  }));

  return {
    items,
    totalCount: data.totalCount ?? items.length,
    nextToken: data.nextToken,
  };
};

export const mapSchoolDashboardResponse = (raw: unknown) => {
  const data = (raw ?? {}) as RawSchoolCampDashboardResponse;
  return {
    summary: {
      activeCamps: data.summary?.activeProgramsCount ?? 0,
      enrolledCampers: data.summary?.enrolledCampersCount ?? 0,
      openSeats: data.summary?.openSeatsCount ?? 0,
      waitlistPressure: data.summary?.waitlistCount ?? 0,
      unpaidBalanceCents: data.summary?.unpaidBalanceCents ?? 0,
      seasonLabel: 'Current camp season',
    },
    items: (data.items ?? []).map((item) => ({
      id: item.program?.id ?? '',
      name: item.program?.name ?? 'Untitled Program',
      campType: formatCampType(item.program?.campType),
      gradeMin: item.program?.minGrade,
      gradeMax: item.program?.maxGrade,
      sessionCount: item.sessions?.length ?? 0,
      enrolledCount: (item.sessions ?? []).reduce((sum, session) => sum + (session.enrolledCount ?? 0), 0),
      totalCapacity: (item.sessions ?? []).reduce((sum, session) => sum + (session.capacity ?? 0), 0),
      waitlistCount: (item.sessions ?? []).reduce((sum, session) => sum + (session.waitlistCount ?? 0), 0),
      status: item.program?.status,
    })),
    ingestHealth: data.ingestHealth
      ? {
          state: data.ingestHealth.state,
          lastRefreshAt: formatDateLabel(data.ingestHealth.lastRefreshAt),
          sourceSystem: data.ingestHealth.sourceSystem,
        }
      : undefined,
    waitlistPressure: {
      totalWaitlisted: data.waitlistPressure?.totalWaitlisted ?? 0,
      sessionsAtCapacity: data.waitlistPressure?.sessionsAtCapacity ?? 0,
    },
    rosterReadiness: {
      readyCount: data.rosterReadiness?.readyPrograms ?? 0,
      pendingCount: data.rosterReadiness?.pendingPrograms ?? 0,
    },
    opsTelemetry: {
      latestRefreshAt: data.opsTelemetry?.latestRefreshAt ? formatDateLabel(data.opsTelemetry.latestRefreshAt) : null,
      lastSnapshotDate: data.opsTelemetry?.lastSnapshotDate ?? null,
      sourceSystem: data.opsTelemetry?.sourceSystem,
    },
    eventResponseSummary: {
      pendingCommunications: data.eventResponseSummary?.pendingCommunications ?? 0,
      pendingPromotions: data.eventResponseSummary?.pendingWaitlistPromotions ?? 0,
    },
  };
};

export const mapSchoolProgramDetailResponse = (raw: unknown) => {
  const data = (raw ?? {}) as RawSchoolCampProgramDetailResponse;
  const sessions = data.sessions ?? [];
  const sessionById = new Map(sessions.map((session) => [session.id ?? '', session]));
  const registrationItems = mapCampRegistrationListResponse(data.registrations).items;

  return {
    program: data.program
      ? {
          id: data.program.id ?? '',
          name: data.program.name ?? 'Camp Program',
          status: data.program.status,
          campType: formatCampType(data.program.campType),
          gradeMin: data.program.minGrade,
          gradeMax: data.program.maxGrade,
          location: data.program.location ?? undefined,
          totalCapacity: sessions.reduce((sum, session) => sum + (session.capacity ?? 0), 0),
          totalEnrolled: sessions.reduce((sum, session) => sum + (session.enrolledCount ?? 0), 0),
          totalWaitlisted: sessions.reduce((sum, session) => sum + (session.waitlistCount ?? 0), 0),
        }
      : undefined,
    sessions: sessions.map((session) => ({
      id: session.id ?? '',
      label: buildSessionLabel(session),
      startDate: session.startDate,
      endDate: session.endDate,
      capacity: session.capacity,
      enrolled: session.enrolledCount,
    })),
    registrations: registrationItems.map((registration) => ({
      id: registration.id,
      studentName: registration.studentName,
      sessionLabel: registration.sessionIds
        .map((sessionId) => buildSessionLabel(sessionById.get(sessionId)))
        .filter(Boolean)
        .join(', '),
      registrationStatus: registration.status === 'registered'
        ? 'confirmed'
        : registration.status === 'pending_payment'
          ? 'pending'
          : registration.status,
      paymentStatus: registration.paymentStatus === 'unpaid'
        ? 'pending'
        : registration.paymentStatus === 'partially_paid'
          ? 'pending'
          : registration.paymentStatus,
    })),
    waitlist: (data.waitlist?.items ?? []).map((entry) => ({
      id: entry.id ?? '',
      position: entry.position,
      studentName: entry.studentName,
      sessionLabel: buildSessionLabel(sessionById.get(entry.sessionId ?? '')),
      status: entry.status === 'active'
        ? 'waiting'
        : entry.status === 'promoted'
          ? 'offered'
          : entry.status,
    })),
    financeSummary: data.financeSummary,
    communicationsSummary: data.communicationsSummary
      ? {
          lastSentAt: formatDateLabel(data.communicationsSummary.lastSentAt),
          pendingCount: data.communicationsSummary.pendingCount ?? 0,
        }
      : undefined,
    ingestHealth: data.ingestHealth
      ? {
          state: data.ingestHealth.state,
          lastRefreshAt: formatDateLabel(data.ingestHealth.lastRefreshAt),
          sourceSystem: data.ingestHealth.sourceSystem,
        }
      : undefined,
    exportAudit: data.exportAudit
      ? {
          lastExportAt: formatDateLabel(data.exportAudit.lastExportAt),
          lastExportFormat: data.exportAudit.lastExportFormat ?? undefined,
          lastExportBy: data.exportAudit.lastExportStatus ?? undefined,
        }
      : undefined,
  };
};

export const mapCampProgramEditorResponse = (raw: unknown) => {
  const data = (raw ?? {}) as RawCampProgramEditorResponse;
  const firstSession = data.sessions?.[0];
  const pricePerSessionCents = data.program?.pricePerSessionCents ?? 0;
  const siblingDiscountPercent = pricePerSessionCents > 0 && (data.program?.siblingDiscountCents ?? 0) > 0
    ? Math.round(((data.program?.siblingDiscountCents ?? 0) / pricePerSessionCents) * 100)
    : 0;

  return {
    program: data.program
      ? {
          id: data.program.id ?? '',
          name: data.program.name ?? '',
          description: data.program.description ?? '',
          campType: data.program.campType ?? '',
          location: data.program.location ?? '',
          gradeMin: data.program.minGrade ?? '',
          gradeMax: data.program.maxGrade ?? '',
          genderEligibility: data.program.genderEligibility ?? '',
          priceCentsPerSession: data.program.pricePerSessionCents ?? 0,
          siblingDiscountPercent,
          multiSessionDiscountPercent: data.program.multiSessionDiscountPct ?? 0,
          capacityPerSession: firstSession?.capacity ?? 0,
          waitlistPolicy: '',
          waiverText: data.program.waiverText ?? '',
          packingList: data.program.packingList ?? '',
          sessions: data.sessions ?? [],
        }
      : undefined,
    sessions: data.sessions ?? [],
    defaults: {
      campTypes: data.defaults?.campTypeOptions ?? [],
      gradeOptions: data.defaults?.gradeOptions ?? [],
      genderOptions: data.defaults?.genderEligibilityOptions ?? [],
      waitlistPolicies: ['auto'],
      locationOptions: [],
    },
    ingestHealth: data.ingestHealth
      ? {
          state: data.ingestHealth.state,
          lastRefreshAt: formatDateLabel(data.ingestHealth.lastRefreshAt),
          sourceSystem: data.ingestHealth.sourceSystem,
        }
      : undefined,
  };
};
